import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { Hash, updateHashProps } from '../general';
import { loadDbModel } from './workBackendSlice';

type SplitRatiosType = { 'userHome': number[]; 'bookEditor': number[]; 'cardBrowser': number[]; 'imageBrowser': number[];  }
type ZoomRatesType = { [key in string]: number | number[] }// key is sheetId
type PlaceType = 'folder' | 'book' | 'sheet'
type NoteDisplayType = { 'bookEditor': Hash<boolean>; 'cardTemplateDesign': boolean; 'card': boolean; }
type WorkNoteRatiosType = { 'bookEditor': Hash<number[]>; 'cardTemplateDesign': number[]; 'card': number[]; }

export type UserCondition = {
  userId: string;
  bookTreeDisplay: boolean;
  splitRatios: SplitRatiosType;
  zoomRates: ZoomRatesType;
  noteDisplay: NoteDisplayType;
  workNoteRatios: WorkNoteRatiosType;
  where: PlaceType;
  when: string;
  folderId?: string;
  bookId?: string;
  sheetId?: string;
}
export const initial: UserCondition = {
  userId: '',
  bookTreeDisplay: false,
  splitRatios: { 'userHome': [50, 50], 'bookEditor': [25, 75], 'cardBrowser': [25, 75], 'imageBrowser': [25, 75] },
  zoomRates: {},
  noteDisplay: { 'bookEditor': {}, 'cardTemplateDesign': false, 'card': false },
  workNoteRatios: { 'bookEditor': {}, 'cardTemplateDesign': [75, 25], 'card': [75, 25] },
  where: 'sheet',
  when: '',
  folderId: undefined,
  bookId: undefined,
  sheetId: undefined
};

export type UserConditionHashType = Hash<UserCondition>

export type UserConditionAddPayload = {
  id?: string;
} & UserCondition;
export type UserConditionUpdatePayload = {
  id: string;
} & { [P in keyof UserCondition]?: UserCondition[P] };

export type UserConditionState = {
  userConditions: UserConditionHashType | null;
}

export const generateUserConditionId = (): string => generateId('uc');
let initialState: UserConditionState = { userConditions: null };
const key = 'userConditions';

const userConditionSlice = createSlice({
  name: 'userCondition',
  initialState,
  reducers: {
    initializeUserCondition(state: UserConditionState) {
      state.userConditions = null;
    },
    addUserCondition(state: UserConditionState, action: PayloadAction<UserConditionAddPayload>) {
      if (!state.userConditions) state.userConditions = {};
      let { id, ...obj } = action.payload;
      if (!id) id = generateUserConditionId();
      state.userConditions[id] = obj;
      const json = JSON.stringify(state.userConditions);
      sessionStorage.setItem(key, json);
      localStorage.setItem(key, json);
    },
    updateUserCondition(state: UserConditionState, action: PayloadAction<UserConditionUpdatePayload>) {
      if (!state.userConditions) return;
      const { id, ...obj } = action.payload;
      if (state.userConditions[id] === undefined) {
        console.log(`*** UserCondition not found: ${id}`);
      } else {
        const newData = { ...state.userConditions[id], ...obj };
        state.userConditions[id] = newData;
        const json = JSON.stringify(state.userConditions);
        sessionStorage.setItem(key, json);
        localStorage.setItem(key, json);
      }
    },
    saveUserConditions(state: UserConditionState) {
      const json = JSON.stringify(state.userConditions);
      localStorage.setItem(key, json);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: UserConditionState, action) => {
      const values = action.payload;
      if (!values) {
        state.userConditions = {};
        return;
      }
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.userConditions = updatedValues;
      if (changed) {
        const json = JSON.stringify(state.userConditions);
        sessionStorage.setItem(key, json);
        localStorage.setItem(key, json);
      }
    })
  }
})

export const { initializeUserCondition, addUserCondition, updateUserCondition, saveUserConditions } = userConditionSlice.actions;
export default userConditionSlice.reducer;
